import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTableApi } from '~Components/TableUniverse';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDispatch, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { BlueButton, Box } from '~Styles';
import { LocalItem } from '~Types';
import * as actions from '~actions';
import { useEntryFinder } from '~Components/EntryFinder';
import { getSelectedOrder_order_items } from '~Scenes/History/__queries__';
import { HistoryOrder } from '~Scenes/History';
import { GetOrder_order } from '~Scenes/History-v2/Components/__queries__';
import { v4 as uuid } from 'uuid';
import { useHistory } from 'react-router';
import { TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType } from '~Scenes/Handy/__queries__';
import HandyTables from './HandyTables';
import TabletTables from './TabletTables';

const AddToTableButton = styled(BlueButton)`
  min-height: 32px;
  font-size: 12px;
  padding: 8px 16px;
`;

interface Props {
  order: HistoryOrder | GetOrder_order;
  onHandyComplete?: (table: TableType, chequeId: string) => void;
}

const SendCopyToTable: FC<Props> = ({ order, onHandyComplete }) => {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<TableType | null>(null);

  const modal = useModal();
  const { i18n } = useI18n();
  const history = useHistory();
  const tableApi = useTableApi();
  const licenses = useLicenses();
  const logger = useLogger('copy-to-table');
  const venueId = useSelector((state) => state.venueId) as string;
  const dispatch = useDispatch();
  const findEntryFromItem = useEntryFinder();

  const isHandy = window.innerWidth <= 600;

  const transformItems = (items: getSelectedOrder_order_items[]) => {
    return items.map((item) => {
      return {
        id: uuid(),
        itemId: item.entry.item.id,
        categoryId: item.entry.item.categories[0].id,
        variations: item.variations.map((variation) => ({
          id: variation.id,
          name: i18n(variation.nameLang),
          price: variation.price,
        })),
        removedVariations: item.removedVariations.map((variation) => ({
          id: variation.id,
          name: i18n(variation.nameLang),
          price: 0,
        })),
        title: i18n(item.entry.item.titleLang),
        titleLang: item.entry.item.titleLang,
        description: i18n(item.entry.item.descriptionLang),
        count: 1,
        message: item.message,
        amount: item.price.amount,
        fraction: item.fraction,
        discount: item.price.discount,
        vatRate: item.entry.item.vatRate,
      };
    });
  };

  /* When a table has been selected, create a cheque and copy items to it */
  useEffect(() => {
    if (!selectedTableId) {
      return;
    }

    const chequeId = tableApi.createCheque(selectedTableId);
    if (!chequeId) {
      dispatch(actions.addToast(i18n('History.SendOrderCopy.NoCheque.Toast'), false, null, true));
      logger.error('The cheque could not be created.');
      return;
    }

    const transformedItems: LocalItem[] = transformItems(order.items).map((item) => ({
      ...item,
      entryId: findEntryFromItem(item.itemId).id,
    }));

    tableApi.addItems(transformedItems, chequeId);

    logger.info('Send copy - Order copied to table', {
      tableId: selectedTableId,
      items: transformedItems,
    });

    const timer = setTimeout(() => {
      if (!licenses.hasTSR) {
        modal.toggle();
        return;
      }

      /* If the device has TSR then navigate to TSR otherwise just close the modal */
      if (isHandy && onHandyComplete && selectedTable) {
        onHandyComplete(selectedTable, chequeId);
      } else {
        history.push(`/venue/${venueId}/tables?table=${selectedTableId}`);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedTableId]);

  const addToTableButton = useButton({
    onClick: () => modal.toggle(),
  });

  return (
    <Box>
      <AddToTableButton {...addToTableButton}>
        {i18n('History.SendOrderCopy.Button')}
      </AddToTableButton>

      {isHandy ? (
        <>
          {modal.isShowing && (
            <HandyTables
              onSelectTable={(table) => {
                setSelectedTable(table);
                setSelectedTableId(table.id);
              }}
              onClose={modal.toggle}
            />
          )}
        </>
      ) : (
        <Modal isShowing={modal.isShowing} hide={modal.toggle} noPadding>
          <TabletTables onSelectTable={(id) => setSelectedTableId(id)} />
        </Modal>
      )}
    </Box>
  );
};

export default SendCopyToTable;
